import { Platform } from 'react-native';
import type { MessagesV3 } from './MessagesV3Parts';
import {
  BUTTON_HOVER, MESSAGE_MENU, PRESENCE_DOT, QUOTE_BAR, QUOTE_FILL, SEND_FILL, SEND_SHADOW,
  V3_EMOJI_INLINE, glass, v3Transition,
} from './messagesV3';

/**
 * CORE-1791 (2b) — переписка раздела сообщений по макету v3: переопределения
 * поверх общих стилей треда (`makeStyles` в AgapellaChatThread) по тем же
 * ключам. Тред общий для шести мест; вне раздела (нет MessagesSurface) этот
 * слой не накладывается, и всё остаётся как было.
 */
type Style = Record<string, unknown>;

export function makeV3ThreadStyles(v3: MessagesV3): Record<string, Style> {
  const { t, g } = v3;
  const font = Platform.OS === 'web' ? { fontFamily: 'var(--ag-font-ui)' } : {};
  const iconBtn: Style = {
    width: g.composerButton, height: g.composerButton, borderRadius: v3.phone ? 12 : 13,
    alignItems: 'center', justifyContent: 'center',
    ...(v3.phone ? {} : { backgroundImage: t.softWell, boxShadow: t.iconShadow }),
    ...v3Transition(v3.reduceMotion),
  };
  // Отступы ленты: контент до 780 по центру, по бокам не меньше 28 (на телефоне 14).
  const sidePad = g.conversationPadH != null ? g.conversationPadH : `max(28px, calc((100% - ${g.contentMax}px) / 2))`;
  const quoted: Style = {
    // Подложка слоем, а не цветом: react-native-web округляет альфу цвета до сотых (.045 → .04).
    borderLeftWidth: 2, borderLeftColor: QUOTE_BAR, backgroundColor: 'transparent', backgroundImage: `linear-gradient(${QUOTE_FILL},${QUOTE_FILL})`, opacity: 1,
    paddingVertical: 3, paddingLeft: g.quotePadH, paddingRight: g.quotePadH, marginBottom: 9,
    borderTopRightRadius: 8, borderBottomRightRadius: 8,
  };
  return {
    root: { backgroundColor: 'transparent' },
    backHeader: {
      position: 'relative', height: g.headerHeight, flexShrink: 0, gap: g.headerGap,
      paddingHorizontal: g.headerPadH, paddingVertical: 0,
      borderBottomWidth: 1, borderBottomColor: 'rgba(240,160,109,.22)',
      backgroundColor: 'transparent', backgroundImage: t.headerBg,
    },
    backHeaderName: { ...font, color: t.text, fontSize: g.headerName.size, lineHeight: g.headerName.line, fontWeight: '600' },
    backHeaderSub: { ...font, color: t.muted, fontSize: 12, lineHeight: 16, marginTop: 2 },
    listContent: {
      paddingTop: g.conversationPadV, paddingBottom: g.conversationPadV,
      paddingLeft: sidePad, paddingRight: sidePad, gap: g.conversationGap,
    },
    row: { marginBottom: 0, alignItems: 'flex-end' },
    bubble: {
      maxWidth: g.bubbleMax, paddingTop: g.bubblePad.t, paddingBottom: g.bubblePad.b, paddingHorizontal: g.bubblePad.h,
      borderWidth: 1, borderColor: t.border,
      borderTopLeftRadius: 18, borderTopRightRadius: 18, borderBottomRightRadius: 18, borderBottomLeftRadius: 6,
      ...glass(12, 'transparent', false),
    },
    groupBubble: { maxWidth: g.bubbleMax },
    bubbleTheirs: {
      backgroundColor: 'transparent', backgroundImage: t.incomingBg, boxShadow: t.incomingShadow,
      borderTopLeftRadius: 18,
    },
    bubbleMine: {
      backgroundColor: t.outgoingBase ?? 'transparent', backgroundImage: t.outgoingBg, boxShadow: t.outgoingShadow,
      borderColor: t.outgoingBorder,
      borderTopRightRadius: 18, borderBottomRightRadius: 6, borderBottomLeftRadius: 18,
    },
    // Эмодзи без пузыря (CORE-1730: одна или две-три): ни заливки, ни рамки, ни размытия.
    bubbleEmojiOnly: {
      backgroundColor: 'transparent', backgroundImage: 'none', boxShadow: 'none', borderWidth: 0,
      paddingTop: 0, paddingBottom: 0, paddingHorizontal: 0, paddingRight: v3.phone ? 4 : 10,
      backdropFilter: 'none', WebkitBackdropFilter: 'none',
    },
    bubbleText: { ...font, color: t.text, fontSize: 16, lineHeight: 24 },
    bubbleTextMine: { color: t.text },
    link: { color: t.accent, textDecorationLine: 'underline', wordBreak: 'break-word', overflowWrap: 'anywhere' },
    linkMine: { color: t.accent },
    mention: { color: t.accent },
    mentionMine: { color: t.accent, textDecorationLine: 'none' },
    groupSender: { color: t.accent },
    // Цитата: левый контур 2px, тёплая подложка, скругление справа.
    quoted,
    // В разделе пузырь «открытый» всегда (openSurface), и стиль CORE-1730 для
    // цитаты без пузыря ложится поверх: его отступы 4/8 перебивали макетные 3/12 и 9.
    quotedEmojiOnly: quoted,
    quotedSender: { ...font, color: t.accent, fontSize: 14, lineHeight: 20, fontWeight: '600' },
    quotedText: { ...font, color: t.muted, fontSize: 14, lineHeight: 20 },
    quotedDeleted: { color: t.muted },
    quotedDeletedMine: { color: t.muted },
    // Время и статус — строкой под текстом/картинкой, справа.
    metaRow: { alignSelf: 'flex-end', gap: 5, marginTop: 6 },
    metaRowStart: { alignSelf: 'flex-end' },
    msgTime: { ...font, fontSize: 12, lineHeight: 16 },
    msgTimeMine: { color: t.muted },
    msgTimeTheirs: { color: t.muted },
    dateSep: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 18, paddingVertical: 0, marginBottom: 3 },
    dateSepText: {
      ...font, fontSize: 12, lineHeight: 16, fontWeight: '400', letterSpacing: 0, color: t.muted,
      backgroundColor: 'transparent', paddingHorizontal: 0, paddingVertical: 0, borderRadius: 0,
    },
    reactionsRow: { marginHorizontal: 0 },
    reactionsTheirs: { marginLeft: 0 },
    reactionChip: { backgroundColor: 'transparent', backgroundImage: t.softWell, borderColor: t.border },
    reactionChipMine: { borderColor: 'rgba(240,160,109,.32)', backgroundColor: 'rgba(233,130,69,.08)' },
    reactionCount: { color: t.muted },
    // «…» у сообщения: спокойный вторичный цвет, без приглушения прозрачностью —
    // необходимый глиф держит 3:1 (при .45 было 2.5).
    menuBtn: { opacity: 1 },
    menuBtnActive: { opacity: 1 },
    // Якорь нулевой ширины у внешнего края пузыря: кнопка от него абсолютно,
    // в гаттер, поэтому пузыри не сдвигаются и не теряют ширину.
    menuSlot: { width: 0, alignSelf: 'stretch', position: 'relative' },
    menuHit: {
      position: 'absolute', bottom: 0, width: MESSAGE_MENU.hit, height: MESSAGE_MENU.hit,
      alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
      transitionProperty: 'opacity', transitionDuration: v3.reduceMotion ? '0s' : `${MESSAGE_MENU.fadeMs}ms`,
    },
    menuHitMine: { right: 0 },
    menuHitTheirs: { left: 0 },
    menuFace: {
      width: MESSAGE_MENU.face, height: MESSAGE_MENU.face, borderRadius: MESSAGE_MENU.faceRadius,
      alignItems: 'center', justifyContent: 'center',
      ...(v3.reduceMotion ? {} : { transitionProperty: 'background-color', transitionDuration: '160ms' }),
    },
    menuFaceHover: { backgroundColor: BUTTON_HOVER },
    attachChip: { backgroundColor: 'transparent', backgroundImage: t.softWell, borderWidth: 1, borderColor: t.border },
    attachChipText: { ...font, color: t.text },
    attachChipIcon: { color: t.accent },
    cardsWrap: { marginLeft: 0, marginRight: 0 },
    thinkingText: { ...font, color: t.muted },
    pinnedBarTitle: { color: t.accent },
    pinnedChipText: { color: t.accent },
    chip: { borderColor: 'rgba(240,160,109,.32)', backgroundImage: t.softWell },
    chipText: { ...font, color: t.accent },
    // Поле ввода.
    composeArea: {
      position: 'relative', paddingTop: g.composeArea.t, paddingHorizontal: g.composeArea.h, paddingBottom: g.composeArea.b,
      backgroundImage: t.composeAreaBg,
    },
    composer: {
      position: 'relative', flexDirection: 'row', alignItems: v3.phone ? 'flex-end' : 'center',
      gap: g.composerGap, padding: g.composerPad, minHeight: g.composerMinHeight, borderRadius: g.composerRadius,
      borderTopWidth: 0, backgroundImage: t.composerBg, boxShadow: t.composerShadow,
      ...glass(18, t.fallbackComposer, false),
    },
    composerInput: {
      ...font, flex: 1, minWidth: 0, backgroundColor: 'transparent', borderRadius: 0,
      paddingVertical: g.draftPad.v, paddingHorizontal: g.draftPad.h,
      minHeight: 40, maxHeight: g.draftMaxHeight, fontSize: 16, lineHeight: 24, color: t.text,
      outlineStyle: 'none',
    },
    attachBtn: iconBtn,
    emojiBtn: iconBtn,
    iconBtnHover: v3.phone ? { backgroundColor: BUTTON_HOVER } : { backgroundColor: t.iconHoverBg, boxShadow: t.iconHoverShadow },
    sendBtn: {
      width: g.sendSize.w, height: g.sendSize.h, borderRadius: g.sendSize.r, marginLeft: v3.phone ? 3 : 0,
      backgroundColor: 'transparent', backgroundImage: SEND_FILL, boxShadow: SEND_SHADOW,
      alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
      ...v3Transition(v3.reduceMotion),
    },
    sendBtnHover: { filter: 'brightness(1.06)' },
    // Макет показывает отправку одинаковой и при пустом черновике; недоступность —
    // через aria-disabled и курсор, без приглушения (прозрачность контейнера запрещена).
    sendBtnDisabled: { opacity: 1, cursor: 'default' },
    recBar: { backgroundColor: 'transparent', minHeight: 40 },
    recTime: { ...font, color: t.text },
    recHint: { ...font, color: t.muted },
    editBanner: { borderTopWidth: 0, backgroundColor: 'transparent', backgroundImage: `linear-gradient(${QUOTE_FILL},${QUOTE_FILL})`, borderRadius: 12, marginBottom: 8 },
    editBannerText: { ...font, color: t.accent },
    replyBannerName: { ...font, color: t.accent, fontSize: 14, lineHeight: 20 },
    replyBannerText: { ...font, color: t.muted, fontSize: 14, lineHeight: 20 },
    // CORE-1791 (возврат QA 23.10) — «Удалить» и «Заблокировать» в меню: общий
    // красный светлой темы на белой карточке меню давал 4.1 при норме 4.5.
    menuLabelDanger: { color: t.danger },
    mutedBar: { borderTopWidth: 0 },
    mutedText: { color: t.muted },
    attachHint: { color: t.muted },
    // Ошибки — терракотовый токен из статьи Live (#9A421F / #F3B39A) и контурный
    // alert рядом: в светлой теме цвет совпадает с акцентными подписями, ошибку
    // отличает форма. Существующие отображения: отказ отправки (черновик
    // возвращается в поле), файл не загрузился, тред не открылся.
    sendNotice: { alignItems: 'center', gap: 6 },
    sendNoticeText: { ...font, color: t.error, fontSize: 13, lineHeight: 18 },
    attachBlocked: { ...font, color: t.error, flex: 1, paddingHorizontal: 0, paddingBottom: 0 },
    errorText: { ...font, color: t.error, marginTop: 0, fontSize: 14, lineHeight: 20 },
    stagedErrorOverlay: {
      backgroundColor: t.fallbackComposer, backgroundImage: `linear-gradient(${t.errorFill},${t.errorFill})`,
      borderWidth: 1, borderColor: t.errorBorder,
    },
    stagedRetryText: { ...font, color: t.error },
    // Крестик «убрать вложение» белый; в тёмной теме общий charcoal светлый, и
    // крестик пропадал на белом кружке (на деве так же). Кружок — charcoal светлой темы (#00121D) в обеих темах.
    stagedRemove: { backgroundColor: '#00121D' },
    stagedStrip: { paddingHorizontal: 4, paddingTop: 0, paddingBottom: 8 },
    // Палитра — всплывающая панель над полем: фон шапки, рамка, единственная тень.
    emojiPicker: {
      position: 'absolute', zIndex: 3, bottom: '100%',
      ...(v3.phone ? { left: 10, right: 10 } : { left: 72, maxWidth: 400 }),
      padding: v3.phone ? 10 : 12, borderRadius: v3.phone ? 18 : 16, borderWidth: 1, borderColor: t.border,
      backgroundColor: t.headBase, backgroundImage: t.head, boxShadow: '0 8px 30px rgba(0,0,0,.133)',
    },
    presenceDot: { width: 5, height: 5, borderRadius: 2.5, backgroundColor: PRESENCE_DOT },
    emojiStamp: { paddingVertical: 4, paddingHorizontal: 6, borderRadius: 9, backgroundColor: t.emojiStampBg, marginTop: 3 },
  };
}

/** Эмодзи в тексте пузыря: 28 и по 2px по бокам (макет), без растяжения строки. */
export const V3_INLINE_EMOJI_STYLE = { marginHorizontal: 2 } as const;
export { V3_EMOJI_INLINE };

/**
 * Наложить переопределения на объект стилей по ключам. Стили треда — плоские
 * объекты (`StyleSheet.create` на вебе возвращает их как есть), поэтому
 * переопределение — обычное слияние полей; ключи, которых нет в базе, добавляются.
 */
export function overlayStyles<T extends Record<string, unknown>>(base: T, over: Record<string, Style>): T & Record<string, Style> {
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(over)) {
    const b = base[k];
    out[k] = b && typeof b === 'object' && !Array.isArray(b) ? { ...(b as object), ...v } : v;
  }
  return out as T & Record<string, Style>;
}
